import { produce } from 'immer';
import { remove } from 'lodash/fp';
import { FetchStatus } from 'app/lib/constants';
import { extractError } from 'app/lib/error';
import { SET_CURRENT_ENTITY } from 'app/store/projects/types.js';
import { RESTORE_TRANSFORMATION_DEPLOYMENT } from 'app/store/deploymentHistory/types';
import { toStringFqn } from 'app/lib/utils';
import { DeployingStatus } from './constants';

import {
    CANCEL_EDIT_TRIGGER,
    CLOSE_DEPLOY_DIALOG,
    CLOSE_HISTORY_DIALOG,
    DEPLOY_AS_TABLE,
    DEPLOY_AS_VIEW,
    EDIT_TRIGGER,
    GET_HISTORY,
    OPEN_DEPLOY_DIALOG,
    SAVE_TRIGGER,
    SHOW_HISTORY_DIALOG
} from './types.js';

export const INITIAL_STATE = {
    deployment: {
        isDialogOpen: false,
        inDeployment: [],
        byRequestId: {},
        viewUuid: null
    },
    error: null,
    isEditingSchedule: false,
    isSavingSchedule: false,
    history: {
        entity: {
            fqn: null,
            deployedSourceId: null,
            type: null
        },
        statusById: {},
        resultsById: {},
        error: null
    }
};

const makeCmp = (database, schema, table) => (c) =>
    c.database === database && c.schema === schema && c.table === table;

export const deployViewReducer = produce((draft, action) => {
    switch (action.type) {
        case OPEN_DEPLOY_DIALOG: {
            const { viewUuid } = action.payload;

            draft.deployment.viewUuid = viewUuid;
            draft.deployment.isDialogOpen = true;
            draft.error = null;
            break;
        }

        case CLOSE_DEPLOY_DIALOG:
            draft.deployment.isDialogOpen = false;
            draft.error = null;
            draft.deployment.viewUuid = null;
            break;

        case DEPLOY_AS_VIEW.STARTED:
        case DEPLOY_AS_TABLE.STARTED: {
            const { transformationId, table, redeploy } = action.payload;
            const { requestId } = action.meta;

            draft.deployment.byRequestId[requestId] = {
                isFirstDeploy: !redeploy,
                status: DeployingStatus.STARTED,
                transformationId,
                table
            };
            draft.error = null;
            break;
        }
        case RESTORE_TRANSFORMATION_DEPLOYMENT.STARTED: {
            const { table } = action.payload;
            const { requestId, transformationId, redeploy } = action.meta;

            draft.deployment.byRequestId[requestId] = {
                isFirstDeploy: !redeploy,
                status: DeployingStatus.STARTED,
                transformationId,
                table
            };
            draft.error = null;
            break;
        }

        case DEPLOY_AS_TABLE.PROGRESS:
        case RESTORE_TRANSFORMATION_DEPLOYMENT.PROGRESS: {
            const { database, schema, table } = action.payload;
            const { requestId } = action.meta;
            const currentDeployingRequest = draft.deployment.byRequestId[requestId];
            currentDeployingRequest.status = DeployingStatus.COMPLETED;

            draft.deployment.inDeployment.push({ database, schema, table });
            draft.deployment.isDialogOpen = false;
            draft.deployment.viewUuid = null;
            break;
        }

        case DEPLOY_AS_VIEW.REJECTED:
        case DEPLOY_AS_VIEW.FAILED:
        case DEPLOY_AS_TABLE.REJECTED:
        case DEPLOY_AS_TABLE.FAILED:
            draft.error = extractError(action.payload);
            break;

        case DEPLOY_AS_VIEW.SUCCEEDED:
            draft.deployment.isDialogOpen = false;
            break;
        case DEPLOY_AS_TABLE.ENDED:
        case DEPLOY_AS_VIEW.ENDED:
        case RESTORE_TRANSFORMATION_DEPLOYMENT.ENDED: {
            const { database, schema, table } = action.payload;
            const { requestId } = action.meta;

            const cmp = makeCmp(database, schema, table);
            draft.deployment.inDeployment = remove(cmp, draft.deployment.inDeployment);

            delete draft.deployment.byRequestId[requestId];
            draft.deployment.viewUuid = null;
            break;
        }

        case EDIT_TRIGGER:
            draft.isEditingSchedule = true;
            break;
        case SET_CURRENT_ENTITY:
        case CANCEL_EDIT_TRIGGER:
            draft.error = null;
            draft.isEditingSchedule = false;
            break;
        case SAVE_TRIGGER.STARTED:
            draft.error = null;
            draft.isSavingSchedule = true;
            break;
        case SAVE_TRIGGER.ENDED:
            draft.isSavingSchedule = false;
            break;
        case SAVE_TRIGGER.SUCCEEDED: {
            draft.isEditingSchedule = false;
            break;
        }
        case SAVE_TRIGGER.FAILED:
            draft.error = extractError(action.payload);
            break;

        case SHOW_HISTORY_DIALOG: {
            const { fqn, deployedSourceId, type } = action.payload;

            draft.history.entity = {
                fqn: typeof fqn !== 'string' ? toStringFqn(fqn) : fqn,
                deployedSourceId,
                type
            };
            break;
        }
        case CLOSE_HISTORY_DIALOG: {
            draft.history.entity = INITIAL_STATE.history.entity;
            break;
        }

        case GET_HISTORY.STARTED: {
            const id = action.meta.fqn;
            draft.history.statusById[id] = FetchStatus.FETCHING;
            draft.history.error = null;
            break;
        }
        case GET_HISTORY.SUCCEEDED: {
            const id = action.meta.fqn;
            draft.history.statusById[id] = FetchStatus.SUCCEEDED;
            draft.history.resultsById[id] = {
                all: action.payload.history,
                next: action.payload.nextRun
            };
            break;
        }
        case GET_HISTORY.FAILED:
        case GET_HISTORY.TIMEDOUT: {
            const id = action.meta.fqn;

            draft.history.statusById[id] = FetchStatus.FAILED;
            draft.history.resultsById[id] = null;
            draft.history.error = extractError(action.payload);
            break;
        }

        default: {
            break;
        }
    }
}, INITIAL_STATE);
